/* LoginModal — text in English and German (same shape in both). */
import { ByLang, useL } from "i18n/lang";

const en = {
  terms: {
    title: "Terms & Conditions",
    intro: "This app is an experimental, personal project exploring sleep and dream sensing — built for learning and exploration, not as a medical product. It plays gentle audio cues during your sleep based on sensor readings; detection won't always be perfect.",
    sections: [
      { title: "What this is", body: "An independent, personal engineering project exploring EOG/eye-movement sensing, sleep-stage estimation, and audio playback timed to detected REM (dreaming) activity." },
      { title: "Not a medical device", body: "Not a certified medical device, and not intended to diagnose, treat, cure, or prevent any medical or psychological condition." },
      { title: "Your own risk, your own choice", body: "Use of this app is voluntary. If you ever feel distressed or unwell, stop using the app and reach out to a trusted person or professional." },
      { title: "No warranty", body: 'Provided "as is," without warranty of any kind. The developer(s) accept no liability for outcomes from its use.' },
    ],
    gotIt: "Got it",
    closeAria: "Close",
  },
  errors: {
    codes: {
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/invalid-email": "Invalid email format.",
      "auth/too-many-requests": "Too many attempts. Try again later.",
      "auth/network-request-failed": "Network error.",
      "auth/invalid-credential": "Invalid email or password.",
    } as Record<string, string>,
    signInFailed: "Sign-in failed. Please try again.",
    agreeTerms: "Please agree to the Terms & Conditions to continue.",
    enterEmailPw: "Enter email and password.",
    pwTooShort: "Password must be at least 6 characters.",
    pwMismatch: "Passwords do not match.",
    updateFailed: "Failed to update password. Please try again.",
    enterEmail: "Enter your email address.",
    noAccount: "No account found with this email.",
    resetFailed: "Failed to send reset email.",
  },
  brandBy: "by",
  contactUs: "Contact Us",
  login: {
    label: "Login",
    headline: "Welcome back",
    email: "Email address",
    password: "Password",
    hidePw: "Hide password",
    showPw: "Show password",
    agreePre: "I agree to the",
    termsLink: "Terms & Conditions",
    loading: "Loading…",
    submit: "Login  →",
    forgot: "Forgot password?",
  },
  forceReset: {
    label: "Reset Required",
    headline: "Set a new password",
    newPw: "New password",
    confirmPw: "Confirm password",
    loading: "Updating…",
    submit: "Update Password",
  },
  forgot: {
    label: "Recover",
    headline: "Reset your password",
    email: "Email address",
    loading: "Sending…",
    submit: "Send Reset Link",
    back: "← Back to login",
  },
  sent: {
    headline: "Email sent",
    check: "Check",
    back: "Back to Login",
  },
};

export type LoginModalText = typeof en;

const de: LoginModalText = {
  terms: {
    title: "Nutzungsbedingungen",
    intro: "Diese App ist ein experimentelles, persönliches Projekt, das Schlaf- und Traumerkennung erforscht – entwickelt zum Lernen und Ausprobieren, nicht als medizinisches Produkt. Sie spielt während deines Schlafs sanfte Audio-Hinweise ab, basierend auf Sensormesswerten; die Erkennung ist nicht immer perfekt.",
    sections: [
      { title: "Was das ist", body: "Ein unabhängiges, persönliches Technikprojekt, das EOG-/Augenbewegungserkennung, die Schätzung von Schlafphasen und Audiowiedergabe passend zu erkannter REM-(Traum-)Aktivität erforscht." },
      { title: "Kein Medizinprodukt", body: "Kein zertifiziertes Medizinprodukt und nicht dazu bestimmt, medizinische oder psychische Erkrankungen zu diagnostizieren, zu behandeln, zu heilen oder ihnen vorzubeugen." },
      { title: "Eigenes Risiko, eigene Entscheidung", body: "Die Nutzung dieser App ist freiwillig. Wenn du dich belastet oder unwohl fühlst, beende die Nutzung und wende dich an eine Vertrauensperson oder eine Fachkraft." },
      { title: "Keine Gewährleistung", body: "Bereitgestellt „wie besehen“, ohne jegliche Gewährleistung. Die Entwickler übernehmen keine Haftung für Folgen der Nutzung." },
    ],
    gotIt: "Verstanden",
    closeAria: "Schließen",
  },
  errors: {
    codes: {
      "auth/user-not-found": "Zu dieser E-Mail gibt es kein Konto.",
      "auth/wrong-password": "Falsches Passwort.",
      "auth/invalid-email": "Ungültiges E-Mail-Format.",
      "auth/too-many-requests": "Zu viele Versuche. Bitte versuch es später noch mal.",
      "auth/network-request-failed": "Netzwerkfehler.",
      "auth/invalid-credential": "E-Mail oder Passwort ist falsch.",
      "auth/user-disabled": "Dieses Konto wurde deaktiviert.",
      "auth/missing-password": "Bitte gib dein Passwort ein.",
    },
    signInFailed: "Anmeldung fehlgeschlagen. Bitte versuch es noch mal.",
    agreeTerms: "Bitte stimme den Nutzungsbedingungen zu, um fortzufahren.",
    enterEmailPw: "Gib E-Mail und Passwort ein.",
    pwTooShort: "Das Passwort muss mindestens 6 Zeichen haben.",
    pwMismatch: "Die Passwörter stimmen nicht überein.",
    updateFailed: "Passwort konnte nicht geändert werden. Bitte versuch es noch mal.",
    enterEmail: "Gib deine E-Mail-Adresse ein.",
    noAccount: "Zu dieser E-Mail gibt es kein Konto.",
    resetFailed: "Die E-Mail zum Zurücksetzen konnte nicht gesendet werden.",
  },
  brandBy: "von",
  contactUs: "Kontakt",
  login: {
    label: "Anmelden",
    headline: "Willkommen zurück",
    email: "E-Mail-Adresse",
    password: "Passwort",
    hidePw: "Passwort verbergen",
    showPw: "Passwort anzeigen",
    agreePre: "Ich stimme den",
    termsLink: "Nutzungsbedingungen zu",
    loading: "Lädt…",
    submit: "Anmelden  →",
    forgot: "Passwort vergessen?",
  },
  forceReset: {
    label: "Neues Passwort nötig",
    headline: "Lege ein neues Passwort fest",
    newPw: "Neues Passwort",
    confirmPw: "Passwort bestätigen",
    loading: "Wird gespeichert…",
    submit: "Passwort ändern",
  },
  forgot: {
    label: "Wiederherstellen",
    headline: "Passwort zurücksetzen",
    email: "E-Mail-Adresse",
    loading: "Wird gesendet…",
    submit: "Link senden",
    back: "← Zurück zur Anmeldung",
  },
  sent: {
    headline: "E-Mail gesendet",
    check: "Schau in dein Postfach:",
    back: "Zurück zur Anmeldung",
  },
};

export const loginModalText: ByLang<LoginModalText> = { en, de };
export const useLoginModalText = () => useL(loginModalText);
